import React, { useEffect, useState } from 'react';
import { Modal, notification, Space, Spin, Upload } from 'antd';
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import ReactPanZoom from 'react-image-pan-zoom-rotate';
import { downloadBlob } from '../common-constants';
import { getStorageService } from '../storage/storage';
import { useTranslation } from 'react-i18next';

const MAX_ORIGINAL_BYTES = 3 * 1024 * 1024; // 3MB

// Progressively shrinks quality/dimensions until under maxBytes (or 6 attempts)
const compressImage = (file, maxBytes = MAX_ORIGINAL_BYTES) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    let { width, height } = img;
    let quality = 0.9;
    let attempts = 0;

    const attempt = () => {
      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => {
        if (!blob) return reject(new Error('Compression failed'));
        attempts += 1;
        if (blob.size <= maxBytes || attempts >= 6) {
          resolve(new File([blob], file.name, { type: file.type }));
        } else {
          quality = Math.max(0.5, quality - 0.1);
          width = Math.round(width * 0.85);
          height = Math.round(height * 0.85);
          attempt();
        }
      }, file.type, quality);
    };
    attempt();
  };
  img.onerror = reject;
  img.src = URL.createObjectURL(file);
});

const ImageUploader = ({
  disabledAdd,
  maxImageSize,
  storageFolder,
  fileList,
  setFileList,
  multiple,
  thumbMaxDimension = 200,
}) => {
  const { t } = useTranslation(); 
  const [uploadInProgress, setUploadInProgress] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [localFileList, setLocalFileList] = useState([]);

  // Initialize storage service
  const storageService = getStorageService(storageFolder);

  const createThumbnail = (file) => {
    const thumbQuality = 1;
    return new Promise((resolve, reject) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      img.onload = () => {
        let { width, height } = img;
        const ratio = Math.min(thumbMaxDimension / width, thumbMaxDimension / height);
        if (ratio < 1) {
          width = width * ratio;
          height = height * ratio;
        }
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => {
          resolve(new File([blob], `thumb_${file.name}`, { type: file.type }));
        }, file.type, thumbQuality);
      };
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  };

  const onPreview = file => {
    if (file && file.url) {
      setPreviewImage(file);
      setPreviewVisible(true);
    }
  };

  useEffect(() => {
    const temp = [...fileList].map(file => ({
      ...file,
      status: 'done',
      thumbUrl: file?.thumbUrl || file?.url
    }));
    setLocalFileList(temp);
  }, [fileList]);

  const customUploadRequest = async (e) => {
    if (!e.file) return;

    setUploadInProgress(true);
    try {
      const originalFile = e.file.size > MAX_ORIGINAL_BYTES ? await compressImage(e.file) : e.file;
      const thumbnail = await createThumbnail(originalFile);
      const { originalUrl, thumbUrl } = await storageService.uploadImages(originalFile, thumbnail);

      setFileList(prev => [...prev, {
        uid: e.file.uid,
        name: originalFile.name,
        url: originalUrl,
        thumbUrl: thumbUrl,
      }]);
    } catch (error) {
      console.log('Upload error:', error);
      notification['error']({
        message: t('common.error', 'Error'),
        description: t('documentUploader.uploadFailed', 'File could not be uploaded.'),
      });
    } finally {
      setUploadInProgress(false);
    }
  };

  const onRemove = async (file) => {
    try {
      await storageService.deleteFiles(file.name);
      notification['info']({
        message: t('common.info', 'Info'),
        description: t('documentUploader.removed', 'File deleted.'),
      });
    } catch (error) {
      console.error("File couldn't be deleted:", error);
    } finally {
      const tempFileList = fileList.filter(f => !(f.uid === file.uid && f.name === file.name));
      setFileList(tempFileList);
    }
  };

  const onDownload = (image) => {
    fetch(image.url, { method: 'GET' })
      .then((response) => response.blob())
      .then((blob) => downloadBlob(blob, image.name));
  };

  return (
    (<div>
      <Upload
        listType="picture-card"
        fileList={localFileList}
        onPreview={onPreview}
        customRequest={customUploadRequest}
        onRemove={onRemove}
        onDownload={onDownload}
        showDownloadIcon
        multiple={multiple}
        disabled={disabledAdd}
        showUploadList={{
          showPreviewIcon: true,
          showDownloadIcon: true,
          showRemoveIcon: !disabledAdd,
        }}
        accept="image/*"
        maxCount={maxImageSize}
      >
        {!disabledAdd && fileList && fileList.length < maxImageSize && (
          <div>
            <Space direction="vertical">
              <PlusOutlined />
              <div className="ant-upload-text">{t('objects.upload')}</div>
              {uploadInProgress && (
                <div>
                  <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                </div>
              )}
            </Space>
          </div>
        )}
      </Upload>
      <Modal
        open={previewVisible}
        footer={previewImage.name}
        onCancel={() => setPreviewVisible(false)}
        closable={false}
        destroyOnHidden
        centered
      >
        <ReactPanZoom image={previewImage.url} alt="image" />
      </Modal>
    </div>)
  );
};

export default ImageUploader;